import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button, Paper } from '@mui/material';
import { FileUploadOutlined } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

// Utils
import { makeManageInventoryColumn } from '../../../utils/columns/makeManageInventoryColumn';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';
import useDateTimeFormat from '../../../hooks/useDateTimeFormat';

// Services
import { Storage } from '../../../utils/Storage';
import { getImportSummaries } from '../../../services/inventory.services';

// Types
import { InventoryContent, SearchBarFilterTypes } from '../../../constants/Constants';
import { InventoryRecord } from '../../../types/InventoryRecord';
import { ImportedFileData } from '../../../types/ImportedFileData';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import CustomModal from '../../common/withModal';
import SearchBar from '../../common/SearchBar/SearchBar';

import ValidationAndSummary from './components/ValidationAndSummary';
import UploadForm from '../../common/forms/Upload/uploadForm';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const useStyles = makeStyles<Theme>((theme) => ({
  heading: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 10px',
    borderBottom: '1px solid #ccc',
    [theme.breakpoints.up('lg')]: {
      height: '60px',
    },
  },
  syncIcon: {
    fontSize: 20,
  },
  autoSyncToolTipTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  autoSyncToolTip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    margin: '10px 0 10px 4px',
  },
  tooltipCursor: { cursor: 'pointer' },
  autoSyncBtn: { textTransform: 'none', marginRight: 6 },
  uploadBtn: { textTransform: 'none' },
  searchContentWrapper: { display: 'flex', flexWrap: 'wrap', marginBottom: 5 },
  createdDateWrapper: { width: '280px', whiteSpace: 'nowrap', paddingRight: '10px' },
  lastSeenDateWrapper: { width: '165px', whiteSpace: 'nowrap', paddingRight: '15px' },
  syncContainer: {
    height: '30px',
    marginLeft: '6px',
  },
  syncSuccess: {
    padding: '4px 10px',
    fontSize: '8px',
    marginLeft: '12px',
    borderRadius: '17px',
    backgroundColor: '#58FF76',
    color: '#fff',
    fontWeight: '700',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#45CC60',
    },
  },
  syncError: {
    padding: '4px 10px',
    fontSize: '8px',
    marginLeft: '12px',
    borderRadius: '17px',
    backgroundColor: '#FFA500',
    color: '#fff',
    fontWeight: '700',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#CC8400',
    },
  },
}));

const ManageInventory = () => {
  const { toStandardDate, timeModeName } = useDateTimeFormat();
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [changedInventoryDetail, setChangedInventoryDetail] = useState<any>();
  const [paramFilter, setParamFilter] = useState<any>({});
  const [isResetting, setIsResetting] = useState(false);

  const [openModalType, setOpenModalType] = useState<(typeof InventoryContent)['uploadLbl'] | (typeof InventoryContent)['summaryLbl'] | null>(null);
  const [importedData, setImportedData] = useState<InventoryRecord | undefined>();

  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const classes = useStyles();

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.DEVICE_INVENTORY);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
        sortBy: filterState.sortBy,
      });
    }
  };

  useEffect(() => {
    intialDefaultFilters();
  }, []);

  useEffect(() => {
    if (isResetting) {
      Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICE_INVENTORY, { filters: [] });
      setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const handleViewSummary = (data: ImportedFileData): void => {
    setChangedInventoryDetail(data);
    setOpenModalType(InventoryContent.summaryLbl);
  };

  const handleReset = () => {
    setIsResetting(true);
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);
  };

  const onSearchChange = (filters: any[]) => {
    // Add date filters
    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      filters.push({
        createdDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          createdDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    // Update state and storage
    setParamFilter({ filters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICE_INVENTORY, { isMoreFilter: true, filters });
  };

  const handleFilesUpdate = (files: any) => {
    setImportedData(files);
    forceReload();
  };

  const columns = useMemo(() => makeManageInventoryColumn(handleViewSummary), []);

  const table = useTable<any>({
    columns,
    fetchAsync: getImportSummaries,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  return (
    <>
      <div className={classes.heading}>
        <h4>{InventoryContent.manageInventoryTitle}</h4>
        <div>
          <Button
            variant='contained'
            size='small'
            className={classes.uploadBtn}
            onClick={() => setOpenModalType(InventoryContent.uploadLbl)}
            endIcon={<FileUploadOutlined className={classes.syncIcon} />}
            sx={tablePrimaryButtonSx}
          >
            {InventoryContent.uploadFile}
          </Button>
        </div>
      </div>
      {importedData && importedData?.length > 0 && (
        <>
          <div className={classes.autoSyncToolTip}>
            <span className={classes.autoSyncToolTipTitle}>{InventoryContent.manageInventorysubtitle}</span>
          </div>
          <div className={classes.syncContainer}>
            <span>
              {toStandardDate(importedData[0]?.createdDateTime)} ({timeModeName})
            </span>
            <Button
              className={importedData[0].errors?.length > 0 ? classes.syncError : classes.syncSuccess}
              onClick={() => handleViewSummary(importedData[0])}
            >
              {importedData[0].errors?.length > 0 ? `${InventoryContent.syncWithErrors}` : `${InventoryContent.syncSuccessful}`}
            </Button>
          </div>
        </>
      )}

      <SearchBar
        pageKey={SearchBarFilterTypes.DEVICE_INVENTORY}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <div className={classes.searchContentWrapper}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FormInputCollection label={`${InventoryContent.createdDate}`}>
                <div style={{ marginRight: '8px' }}>
                  <DatePicker
                    label='From'
                    value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                    format={shortDateFormat}
                    sx={{ width: '185px' }}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => setLastSeenDateTimeFrom(date ? date.toDate() : null)}
                  />
                </div>
                <DatePicker
                  label='To'
                  value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => setLastSeenDateTimeTo(date ? date.toDate() : null)}
                />
              </FormInputCollection>
            </LocalizationProvider>
          </div>
        }
      />
      {openModalType === InventoryContent.summaryLbl && (
        <CustomModal
          isOpen={!!changedInventoryDetail}
          setIsOpen={() => setOpenModalType(null)}
          customAction={() => (
            <Button variant='contained' onClick={() => setOpenModalType(null)} color='secondary' size='small'>
              {InventoryContent.close}
            </Button>
          )}
          showSaveButton={false}
          maxWidth='md'
        >
          <ValidationAndSummary importedFileData={changedInventoryDetail} />
        </CustomModal>
      )}
      {openModalType === InventoryContent.uploadLbl && (
        <CustomModal
          title={InventoryContent.upload}
          isOpen={openModalType === InventoryContent.uploadLbl}
          setIsOpen={() => setOpenModalType(null)}
          maxWidth='md'
          customAction={() => (
            <Button variant='contained' onClick={() => setOpenModalType(null)} color='secondary' size='small'>
              {InventoryContent.close}
            </Button>
          )}
        >
          <UploadForm onFilesUpdate={handleFilesUpdate} fileType='excel' />
        </CustomModal>
      )}
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};

export default ManageInventory;
